import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../elements/Pagination";
import DateRangePicker from "../elements/DateRangePicker";
import plusIcon from "../../resources/icons/plus.svg";
import filterIcon from "../../resources/icons/filter.svg";
import downArrowIcon from "../../resources/icons/down_pointer.svg";
import searchIcon from "../../resources/icons/search.svg";
import xIcon from "../../resources/icons/x.svg";

import dayjs from "dayjs";
import axios from "axios";
import Modal from "../elements/Modal";

const AdminManagement: React.FC = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [tempSearch, setTempSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [dateRangeResetKey, setDateRangeResetKey] = useState(0);

  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const [userTypeFilter, setUserTypeFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [deletedUser, setDeletedUser] = useState<any>(null);

  const [users, setUsers] = useState<any[]>([]);

  const [allCount, setAllCount] = useState(0);
  const [assignorsCount, setAssignorsCount] = useState(0);
  const [contractorsCount, setContractorsCount] = useState(0);
  const [supervisorCount, setSupervisorCount] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const [sortOrder, setSortOrder] = useState<string>("");

  const handleSortByName = () => {
    if (sortOrder === "") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("");
    }
  };

  const handleCreateUser = () => {
    navigate("/admin/management/create-user");
  };

  const handleEditUser = (id: number) => {
    navigate(`/admin/management/edit-user/${id}`);
  };

  const handleDeleteClick = (user: any) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem("token");
    if (!userToDelete || !token) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_URL}/admin/users/${userToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        }
      );
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setDeletedUser(userToDelete);
    } catch {
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
      setShowDeletedModal(true);
    }
  };

  const handleUserTypeFilter = (newType: string) => {
    setUserTypeFilter(newType);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    const filters: string[] = [];
    setSearch(tempSearch);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);

    if (tempSearch) filters.push(`User: ${tempSearch}`);
    if (tempStartDate && tempEndDate) {
      if (tempStartDate === tempEndDate) {
        filters.push(`From: ${tempStartDate}`);
      } else {
        filters.push(`From: ${tempStartDate}`, `To: ${tempEndDate}`);
      }
    } else if (tempStartDate) {
      filters.push(`From: ${tempStartDate}`);
    } else if (tempEndDate) {
      filters.push(`To: ${tempEndDate}`);
    }

    setSelectedFilters(filters);
    setCurrentPage(1);
    setShowFilters(false);
    setTempSearch("");
    setTempStartDate("");
    setTempEndDate("");
  };

  const clearFilters = () => {
    setTempSearch("");
    setTempStartDate("");
    setTempEndDate("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setSelectedFilters([]);
    setUserTypeFilter("");
    setSortOrder("");
    setCurrentPage(1);
    setDateRangeResetKey((prev) => prev + 1);
  };

  const removeFilter = (indexToRemove: number) => {
    const newFilters = [...selectedFilters];
    const removed = newFilters.splice(indexToRemove, 1)[0];
    const key = removed.split(":")[0].trim();

    if (key === "User") setSearch("");
    if (key === "From") setStartDate("");
    if (key === "To") setEndDate("");

    setSelectedFilters(newFilters);
    setCurrentPage(1);
    setTempSearch("");
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", currentPage.toString());
        queryParams.append("limit", rowsPerPage.toString());

        if (userTypeFilter) {
          queryParams.append("userType", userTypeFilter);
        }
        if (search) {
          queryParams.append("name", search);
        }
        if (startDate && endDate) {
          queryParams.append("registerStartDate", startDate);
          queryParams.append("registerEndDate", endDate);
        }

        if (sortOrder) {
          queryParams.append("sort", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_URL}/admin/users?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client-Type": "web",
            },
          }
        );

        const data = res.data;
        setAllCount(data.userTypesCount.all);
        setAssignorsCount(data.userTypesCount.assignors);
        setContractorsCount(data.userTypesCount.contractors);
        setSupervisorCount(data.userTypesCount.supervisors);

        const formattedUsers = data.users.map((u: any) => ({
          ...u,
          role:
            u.type === "ASSIGNOR"
              ? "Assignor"
              : u.type === "CONTRACTOR"
              ? "Contractor"
              : "Construction Supervisor",
          registryDate: u.memberSince,
        }));
        setUsers(formattedUsers);
        setTotalPages(data.totalPages);
      } catch {}
    };

    fetchUsers();
  }, [currentPage, search, startDate, endDate, userTypeFilter, sortOrder]);

  return (
    <div className="flex flex-row">
      <div className="flex-1 p-6 ml-[17.5rem]">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">User Management</h1>
          <button
            onClick={handleCreateUser}
            className="flex items-center bg-buttonBlue text-white rounded px-4 py-2 gap-2"
          >
            <img src={plusIcon} alt="Add" className="w-5 h-5" />
            Create User
          </button>
        </div>

        <div className="mb-4 border-b-2 pb-4">
          <div className="flex items-center justify-between py-4 rounded">
            <div className="flex space-x-6">
              <button
                onClick={() => handleUserTypeFilter("")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  userTypeFilter === "" ? "bg-[#FEF0E8]" : "bg-[#F4F4F4]"
                }`}
              >
                <span
                  className={`font-semibold text-sm ${
                    userTypeFilter === ""
                      ? "text-orange-500 font-bold"
                      : "text-sm"
                  }`}
                >
                  All
                </span>
                <span className="bg-[#F9FAFB] border border-[#EAECF0] px-2 py-0.5 text-sm rounded-full">
                  {allCount}
                </span>
              </button>

              <button
                onClick={() => handleUserTypeFilter("ASSIGNOR")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  userTypeFilter === "ASSIGNOR"
                    ? "bg-[#FEF0E8]"
                    : "bg-[#F4F4F4]"
                }`}
              >
                <span
                  className={`font-semibold text-sm ${
                    userTypeFilter === "ASSIGNOR"
                      ? "text-orange-500 font-bold"
                      : ""
                  }`}
                >
                  Assignors
                </span>
                <span className="bg-[#F9FAFB] border border-[#EAECF0] px-2 py-0.5 text-sm rounded-full">
                  {assignorsCount}
                </span>
              </button>

              <button
                onClick={() => handleUserTypeFilter("CONTRACTOR")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  userTypeFilter === "CONTRACTOR"
                    ? "bg-[#FEF0E8]"
                    : "bg-[#F4F4F4]"
                }`}
              >
                <span
                  className={`font-semibold text-sm ${
                    userTypeFilter === "CONTRACTOR"
                      ? "text-orange-500 font-bold"
                      : ""
                  }`}
                >
                  Contractors
                </span>
                <span className="bg-[#F9FAFB] border border-[#EAECF0] px-2 py-0.5 text-sm rounded-full">
                  {contractorsCount}
                </span>
              </button>

              <button
                onClick={() => handleUserTypeFilter("SUPERVISOR")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  userTypeFilter === "SUPERVISOR"
                    ? "bg-[#FEF0E8]"
                    : "bg-[#F4F4F4]"
                }`}
              >
                <span
                  className={`font-semibold text-sm ${
                    userTypeFilter === "SUPERVISOR"
                      ? "text-orange-500 font-bold"
                      : ""
                  }`}
                >
                  Construction Supervisor
                </span>
                <span className="bg-[#F9FAFB] border border-[#EAECF0] px-2 py-0.5 text-sm rounded-full">
                  {supervisorCount}
                </span>
              </button>
            </div>

            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center border-2 px-4 py-2 rounded-md gap-2"
            >
              <img src={filterIcon} alt="Filter" className="w-5 h-5" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="relative">
              <div className="absolute left-0 top-0 bg-white p-4 border rounded space-y-4 z-50 w-full">
                <div className="flex gap-[1.5rem] pb-4 border-b border-[#D0D5DD]">
                  <div className="flex flex-col w-[22rem]">
                    <label className="text-sm font-medium text-[#344054] mb-1">
                      User
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <img
                          src={searchIcon}
                          alt="Search"
                          className="w-5 h-5 text-[#667085]"
                        />
                      </span>
                      <input
                        type="text"
                        placeholder="Search by user name..."
                        value={tempSearch}
                        onChange={(e) => setTempSearch(e.target.value)}
                        className="pl-10 pr-3 py-2 border border-[#D0D5DD] rounded w-full text-sm shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label className="text-sm font-medium text-[#344054] mb-1">
                      Date of registry
                    </label>
                    <DateRangePicker
                      key={dateRangeResetKey}
                      onChange={(start, end) => {
                        setTempStartDate(start);
                        setTempEndDate(end);
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowFilters(false);
                      setTempSearch("");
                      setTempStartDate("");
                      setTempEndDate("");
                    }}
                    className="px-4 py-2 text-[#1672EC]"
                  >
                    Close
                  </button>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-[#344054] font-semibold"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-4 py-2 text-white bg-[#1672EC] rounded"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {selectedFilters.length > 0 && (
          <div className="px-6 py-3 bg-white border border-[#EAECF0] border-b-0 rounded-t-xl flex justify-between items-center">
            <div className="flex flex-wrap gap-2">
              {selectedFilters.map((filter, index) => (
                <div
                  key={index}
                  className="flex items-center bg-[#FEF0E8] text-[#FC6A19] px-3 border rounded-full border-[#F9DBAF]"
                >
                  <span className="font-semibold mr-2 pr-2 border-r border-[#F9DBAF]">
                    {filter.split(":")[0]}
                  </span>
                  <span className="font-normal">{filter.split(":")[1]}</span>
                  <button onClick={() => removeFilter(index)} className="ml-2">
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button onClick={clearFilters} className="text-buttonBlue">
              Clear Filters
            </button>
          </div>
        )}

        <div
          className={`relative overflow-x-auto border border-[#EAECF0] mb-4 ${
            selectedFilters.length > 0 ? "rounded-b-xl" : "rounded-xl"
          }`}
        >
          <table className="min-w-full table-fixed">
            <thead>
              <tr className="border-b border-[#EAECF0]">
                <th className="flex flex-row gap-2 items-center px-5 py-3 text-left text-xs font-medium text-[#475467]">
                  User
                  <button onClick={handleSortByName}>
                    <img
                      src={downArrowIcon}
                      alt="Sort"
                      className={`w-4 h-4 transform transition-transform duration-200  ${
                        sortOrder === "asc" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475467]">
                  Role
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475467]">
                  Date of Registry
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475467]">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {users.map((user, index) => (
                <tr
                  key={user.id}
                  className={index % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"}
                >
                  <td className="px-5 py-4 text-sm">
                    <div
                      className="font-medium text-[#101828] truncate max-w-[12rem]"
                      title={user.name}
                    >
                      {user.name}
                    </div>
                    <div
                      className="text-[#475467] text-sm truncate max-w-[12rem]"
                      title={user.email}
                    >
                      {user.email}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span
                      className={`text-xs font-medium px-2 py-[2px] rounded-full whitespace-nowrap ${
                        user.role === "Assignor"
                          ? "text-[#FC6A19] bg-[#FEF6EE] border border-[#FEF0E8]"
                          : user.role === "Contractor"
                          ? "text-[#026AA2] bg-[#F0F9FF] border border-[#B9E6FE]"
                          : "text-[#5925DC] bg-[#F4F3FF] border border-[#D9D6FE]"
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-sm text-[#475467]">
                    {dayjs(user.registryDate).format("MMM D, YYYY")}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-sm text-[#475467]">
                    {user.phone}
                  </td>
                  <td className="py-4 whitespace-nowrap text-sm text-[#475467]">
                    <button
                      onClick={() => handleDeleteClick(user)}
                      className="px-3 py-1 text-[#475467] font-semibold"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleEditUser(user.id)}
                      className="px-3 py-1 text-buttonBlue font-semibold"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="border-t mb-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>

        {showDeleteModal && userToDelete && (
          <Modal isOpen={showDeleteModal} maxWidth="32rem">
            <div className="">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-[#FEE4E2]">
                  <img src={xIcon} alt="X Icon" className="w-6 h-4" />
                </div>
                <h2 className="text-xl font-bold mb-4">Delete user?</h2>
                <p className="text-sm text-[#475467] mb-4">
                  You’re about to delete the account of
                  <strong className="ml-1 font-semibold">
                    {userToDelete.name}
                  </strong>
                  .<br />
                </p>
                <p className="text-sm text-[#475467]">Are you sure?</p>
              </div>
              <div className="w-full border-t border-[#EAECF0] px-6 py-6 flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full px-4 py-2 border text-[#344054] border-[#D0D5DD] rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="w-full px-4 py-2 bg-[#D92D20] text-white rounded-md font-semibold"
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showDeletedModal && deletedUser && (
          <Modal isOpen={showDeletedModal} maxWidth="32rem">
            <div className="">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-[#FEE4E2]">
                  <img src={xIcon} alt="X Icon" className="w-6 h-4" />
                </div>
                <h2 className="text-xl font-bold mb-4">User deleted!</h2>
                <p className="text-sm text-[#475467]">
                  You’ve successfully deleted the account of
                  <strong className="ml-1 font-semibold">
                    {deletedUser.name}
                  </strong>
                  .
                </p>
              </div>
              <div className="w-full border-t border-[#EAECF0] px-6 py-6">
                <button
                  onClick={() => {
                    setShowDeletedModal(false);
                    setDeletedUser(null);
                    navigate("/admin/management");
                  }}
                  className="w-full px-4 py-2 bg-buttonBlue text-white rounded"
                >
                  Go to user management
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
